import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { apiErrorMessage } from "../../lib/api";
import PublicHeader from "../../components/layout/PublicHeader";
import PublicFooter from "../../components/layout/PublicFooter";
import StatusBadge from "../../components/StatusBadge";
import CheckoutPanel from "../../components/CheckoutPanel";
import VideoGallery from "../../components/VideoGallery";
import { formatCLP, formatUSD, resolveImageUrl, computeSale, AVX_FORMATS } from "../../lib/format";
import { trackEvent } from "../../lib/analytics";
import { setSeo, setJsonLd } from "../../lib/seo";
import ProductReviews from "../../components/ProductReviews";
import { StarDisplay } from "../../components/StarRating";

export default function ProductDetail() {
    const { slug } = useParams();
    const [product, setProduct] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get(`/products/${slug}`)
            .then(({ data }) => {
                setProduct(data);
                setSeo({
                    title: data.name,
                    description: data.short_description || `${data.name} — plugin de audio AVX Pro (VST3, AU, AAX) para Windows y macOS.`,
                    image: data.image_url ? resolveImageUrl(data.image_url) : undefined,
                });
                const origin = window.location.origin;
                const usd = Number(data.price_usd) || 0;
                const clp = Number(data.price_clp) || 0;
                const offer = {
                    "@type": "Offer",
                    price: (usd > 0 ? usd : clp).toFixed(2),
                    priceCurrency: usd > 0 ? "USD" : "CLP",
                    availability: data.status === "coming_soon" ? "https://schema.org/PreOrder" : "https://schema.org/InStock",
                    url: `${origin}/plugins/${data.slug}`,
                };
                setJsonLd([
                    {
                        "@context": "https://schema.org", "@type": "Product",
                        name: data.name,
                        image: data.image_url ? [resolveImageUrl(data.image_url)] : undefined,
                        description: data.short_description || data.full_description || data.name,
                        brand: { "@type": "Brand", name: "AVX Pro" },
                        category: (data.categories || []).map((c) => c.name).join(", ") || undefined,
                        aggregateRating: (data.rating && data.rating.count > 0) ? {
                            "@type": "AggregateRating",
                            ratingValue: data.rating.average,
                            reviewCount: data.rating.count,
                        } : undefined,
                        offers: offer,
                    },
                    {
                        "@context": "https://schema.org", "@type": "BreadcrumbList",
                        itemListElement: [
                            { "@type": "ListItem", position: 1, name: "Plugins", item: `${origin}/plugins` },
                            { "@type": "ListItem", position: 2, name: data.name, item: `${origin}/plugins/${data.slug}` },
                        ],
                    },
                ]);
                trackEvent("view_item", {
                    items: [{
                        item_id: data.id,
                        item_name: data.name,
                        item_category: "plugin",
                        price: data.price_clp,
                        currency: "CLP",
                    }],
                });
            })
            .catch((e) => setError(apiErrorMessage(e)));
    }, [slug]);

    if (error) {
        return (
            <div className="min-h-screen flex flex-col">
                <PublicHeader />
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                        <p className="text-avx-danger mb-4">{error}</p>
                        <Link to="/plugins" className="btn-secondary px-5 py-2 rounded-sm text-sm font-mono uppercase tracking-[0.18em]">
                            Back to catalog
                        </Link>
                    </div>
                </div>
                <PublicFooter />
            </div>
        );
    }

    if (!product) {
        return (
            <div className="min-h-screen flex flex-col">
                <PublicHeader />
                <div className="flex-1 flex items-center justify-center text-avx-muted font-mono">Loading plugin…</div>
                <PublicFooter />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col" data-testid="product-detail-page">
            <PublicHeader />
            <section className="border-b border-avx-line relative overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                    {product.image_url && <img src={resolveImageUrl(product.image_url)} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="absolute inset-0 bg-gradient-to-b from-avx-bg/85 to-avx-bg" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-14 grid lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <Link to="/plugins" className="label-mono hover:text-avx-blue inline-block mb-6">← Catalog</Link>
                        <div className="flex items-center gap-3 mb-4">
                            <StatusBadge status={product.status} />
                            <span className="label-mono">v{product.current_version}</span>
                        </div>
                        <h1 className="font-heading font-black text-4xl lg:text-5xl tracking-tighter mb-4">{product.name}</h1>
                        {(product.categories || []).length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-5" data-testid="detail-categories">
                                {(product.categories || []).map((c) => (
                                    <Link
                                        key={c.id}
                                        to={`/plugins/categoria/${c.slug}`}
                                        data-testid={`detail-category-${c.slug}`}
                                        className="px-2.5 py-1 rounded-sm border border-avx-blue/40 bg-avx-blue/10 text-avx-blue text-xs font-mono uppercase tracking-[0.15em] hover:bg-avx-blue/20 transition-colors"
                                    >
                                        {c.name}
                                    </Link>
                                ))}
                            </div>
                        )}
                        {product.rating && product.rating.count > 0 && (
                            <a href="#reviews" className="inline-flex mb-5 hover:opacity-80" data-testid="detail-rating-link">
                                <StarDisplay value={product.rating.average} count={product.rating.count} size={16} />
                            </a>
                        )}
                        <p className="text-avx-muted text-lg leading-relaxed mb-8">{product.short_description}</p>
                        <div className="flex flex-wrap gap-2 mb-8">
                            {AVX_FORMATS.map((f) => (
                                <span key={f} className="px-2.5 py-1 rounded-sm border border-avx-blue/40 bg-avx-surface text-avx-blue text-xs font-mono uppercase tracking-[0.2em]">{f}</span>
                            ))}
                            {(product.operating_systems || []).map((o) => (
                                <span key={o} className="px-2.5 py-1 rounded-sm border border-avx-line bg-avx-surface text-avx-text text-xs font-mono uppercase tracking-[0.2em]">{o}</span>
                            ))}
                        </div>
                        <div className="flex items-end gap-6 mb-8">
                            {product.status === "coming_soon" ? (
                                <div className="font-heading font-bold text-2xl text-avx-info">Coming soon</div>
                            ) : !((product.price_clp || 0) > 0) && !((product.price_usd || 0) > 0) ? (
                                <div className="font-heading font-black text-3xl lg:text-4xl text-avx-success" data-testid="detail-free-badge">FREE</div>
                            ) : computeSale(product) ? (
                                <>
                                    <div className="flex items-baseline gap-3">
                                        <div className="font-heading font-black text-3xl lg:text-4xl text-avx-danger" data-testid="detail-sale-price">{formatCLP(computeSale(product).sale_clp)}</div>
                                        <div className="font-mono text-lg text-avx-muted line-through" data-testid="detail-base-price">{formatCLP(computeSale(product).base_clp)}</div>
                                    </div>
                                    <div className="flex flex-col items-start gap-1 pb-1">
                                        <span className="bg-avx-danger text-white font-heading font-black text-sm px-2 py-1 rounded-sm" data-testid="detail-sale-badge">
                                            −{computeSale(product).discount_pct}%
                                        </span>
                                        <div className="font-mono text-base text-avx-muted">{formatUSD(computeSale(product).sale_usd)}</div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="font-heading font-black text-3xl lg:text-4xl text-avx-blue">{formatCLP(product.price_clp)}</div>
                                    <div className="font-mono text-base text-avx-muted pb-1">{formatUSD(product.price_usd)}</div>
                                </>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-3">
                            <a href="#comprar" className="btn-primary px-6 py-3 text-xs font-mono uppercase tracking-[0.2em] rounded-sm" data-testid="detail-cta-buy">
                                {!((product.price_clp || 0) > 0) && !((product.price_usd || 0) > 0) ? "Get for free" : "Buy now"}
                            </a>
                            <Link to="/login" className="btn-secondary px-6 py-3 text-xs font-mono uppercase tracking-[0.2em] rounded-sm">
                                Go to my account
                            </Link>
                        </div>
                    </div>
                    <div className="panel-elevated p-2 aspect-[4/3] overflow-hidden">
                        {product.image_url ? (
                            <img src={resolveImageUrl(product.image_url)} alt={product.name} className="w-full h-full object-cover rounded-sm" />
                        ) : (
                            <div className="w-full h-full bg-gradient-to-br from-avx-elevated to-avx-bg" />
                        )}
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-3 gap-10">
                <div className="lg:col-span-2 space-y-10">
                    <div>
                        <div className="label-mono mb-3">/ Description</div>
                        <p className="text-base leading-relaxed text-avx-text/90">{product.full_description}</p>
                    </div>
                    {product.features?.length > 0 && (
                        <div>
                            <div className="label-mono mb-3">/ Key features</div>
                            <ul className="space-y-2">
                                {product.features.map((f, i) => (
                                    <li key={i} className="flex items-start gap-3 text-sm">
                                        <span className="kpi-tick mt-1.5 shrink-0" />
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    <VideoGallery videos={product.youtube_videos} title="/ Videos" testIdPrefix={`product-videos-${product.slug}`} />
                </div>
                <aside className="space-y-6" id="comprar">
                    <CheckoutPanel product={product} />
                    <div className="panel p-6">
                        <div className="label-mono mb-4">/ Product information</div>
                        <dl className="text-sm space-y-3">
                            <div className="flex justify-between border-b border-avx-line pb-2">
                                <dt className="text-avx-muted">Version</dt>
                                <dd className="font-mono">{product.current_version}</dd>
                            </div>
                            <div className="flex justify-between border-b border-avx-line pb-2">
                                <dt className="text-avx-muted">Formats</dt>
                                <dd className="font-mono">{AVX_FORMATS.join(" · ")}</dd>
                            </div>
                            <div className="flex justify-between border-b border-avx-line pb-2">
                                <dt className="text-avx-muted">Systems</dt>
                                <dd className="font-mono">{(product.operating_systems || []).join(" · ")}</dd>
                            </div>
                            <div className="flex justify-between">
                                <dt className="text-avx-muted">Status</dt>
                                <dd><StatusBadge status={product.status} /></dd>
                            </div>
                        </dl>
                    </div>
                    {product.system_requirements?.length > 0 && (
                        <div className="panel p-6">
                            <div className="label-mono mb-4">/ System requirements</div>
                            <ul className="text-sm space-y-2 text-avx-text/90">
                                {product.system_requirements.map((r, i) => <li key={i}>· {r}</li>)}
                            </ul>
                        </div>
                    )}
                </aside>
            </section>

            <section className="max-w-7xl mx-auto px-6 lg:px-10 w-full pb-10">
                <ProductReviews productId={product.id} productSlug={product.slug} />
            </section>

            <PublicFooter />
        </div>
    );
}
